// =============================================================================
// COMPARAISON CHANTIERS CHART - Bar chart horizontal rentabilité
// =============================================================================

import { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
} from 'recharts';
import { Info, Target } from 'lucide-react';
import { ComparaisonChantierItem } from '@/hooks/useGlobalChartData';
import {
  formatMontantChart,
  formatPourcentage,
  tooltipContainerStyle,
  CHART_STATUS_COLORS,
} from '@/components/charts/chartConfig';

interface Props {
  data: ComparaisonChantierItem[];
}

// =============================================================================
// TOOLTIP PERSONNALISÉ
// =============================================================================

function CustomTooltip({ active, payload }: any) {
  if (!active || !payload || !payload.length) return null;

  const item = payload[0].payload as ComparaisonChantierItem;

  return (
    <div style={tooltipContainerStyle} className="min-w-[280px]">
      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-white/10">
        <div
          className="w-3 h-3 rounded-full"
          style={{ backgroundColor: item.couleur }}
        />
        <span className="text-sm font-medium text-white/90">{item.nom}</span>
      </div>

      <div className="space-y-2">
        <div className="flex justify-between">
          <span className="text-sm text-white/70">Rentabilité</span>
          <span className="text-lg font-bold text-white">
            {formatPourcentage(item.rentabilite)}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-sm text-white/70">Marge</span>
          <span className="text-sm font-medium text-white">
            {Math.round(item.marge).toLocaleString('fr-FR')} €
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-sm text-white/70">Revenus</span>
          <span className="text-sm text-white/60">
            {Math.round(item.revenus).toLocaleString('fr-FR')} €
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-sm text-white/70">Coûts</span>
          <span className="text-sm text-white/60">
            {Math.round(item.couts).toLocaleString('fr-FR')} €
          </span>
        </div>
      </div>

      <div className="mt-3 pt-2 border-t border-white/10">
        <p className="text-xs text-white/50">
          {item.rentabilite >= 18
            ? '✅ Au-dessus de l\'objectif BTP (18%)'
            : item.rentabilite >= 10
            ? '⚠️ En dessous de l\'objectif BTP (18%)'
            : item.rentabilite >= 0
            ? '⚠️ Rentabilité faible'
            : '❌ Chantier en perte'}
        </p>
      </div>
    </div>
  );
}

// =============================================================================
// COMPOSANT PRINCIPAL
// =============================================================================

export function ComparaisonChantiersChart({ data }: Props) {
  // Limiter à 10 chantiers max pour lisibilité
  const displayData = useMemo(() => {
    return data.slice(0, 10);
  }, [data]);

  // Statistiques
  const stats = useMemo(() => {
    const auDessusObjectif = data.filter(c => c.rentabilite >= 18).length;
    const enPerte = data.filter(c => c.rentabilite < 0).length;
    const moyenneRentabilite = data.length > 0
      ? data.reduce((sum, c) => sum + c.rentabilite, 0) / data.length
      : 0;

    return { auDessusObjectif, enPerte, moyenneRentabilite };
  }, [data]);

  if (data.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-muted-foreground">
        <Info className="h-12 w-12 mb-4 opacity-40" />
        <p className="text-sm">Aucun chantier à comparer</p>
        <p className="text-xs mt-1 opacity-70">Les données apparaîtront avec des chantiers actifs</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Stats en haut */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="p-4 rounded-xl bg-card border border-border">
          <p className="text-xs text-muted-foreground mb-1">Au-dessus objectif</p>
          <p className="text-xl font-bold text-emerald-400">
            {stats.auDessusObjectif} / {data.length}
          </p>
          <p className="text-xs text-muted-foreground mt-1">≥ 18% rentabilité</p>
        </div>
        <div className="p-4 rounded-xl bg-card border border-border">
          <p className="text-xs text-muted-foreground mb-1">Moyenne rentabilité</p>
          <p className={`text-xl font-bold ${stats.moyenneRentabilite >= 18 ? 'text-emerald-400' : stats.moyenneRentabilite >= 0 ? 'text-amber-400' : 'text-rose-400'}`}>
            {formatPourcentage(stats.moyenneRentabilite)}
          </p>
        </div>
        <div className="p-4 rounded-xl bg-card border border-border">
          <p className="text-xs text-muted-foreground mb-1">En perte</p>
          <p className={`text-xl font-bold ${stats.enPerte > 0 ? 'text-rose-400' : 'text-emerald-400'}`}>
            {stats.enPerte}
          </p>
          <p className="text-xs text-muted-foreground mt-1">chantier{stats.enPerte > 1 ? 's' : ''}</p>
        </div>
      </div>

      {/* Graphique */}
      <div className="flex-1 min-h-[300px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={displayData}
            layout="vertical"
            margin={{ top: 10, right: 30, left: 100, bottom: 10 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="hsl(0 0% 25%)"
              strokeOpacity={0.3}
              horizontal={true}
              vertical={false}
            />

            <XAxis
              type="number"
              domain={['dataMin - 5', 'dataMax + 5']}
              axisLine={false}
              tickLine={false}
              tick={{ fill: 'hsl(0 0% 60%)', fontSize: 12 }}
              tickFormatter={(value) => `${value.toFixed(0)}%`}
            />

            <YAxis
              type="category"
              dataKey="nom"
              axisLine={false}
              tickLine={false}
              tick={{ fill: 'hsl(0 0% 80%)', fontSize: 11 }}
              width={90}
            />

            <Tooltip content={<CustomTooltip />} />

            {/* Ligne de référence objectif BTP 18% */}
            <ReferenceLine
              x={18}
              stroke={CHART_STATUS_COLORS.excellent}
              strokeDasharray="5 5"
              strokeWidth={2}
              label={{
                value: 'Objectif 18%',
                position: 'top',
                fill: CHART_STATUS_COLORS.excellent,
                fontSize: 10,
              }}
            />

            {/* Ligne de référence 0% */}
            <ReferenceLine
              x={0}
              stroke="hsl(0 0% 50%)"
              strokeWidth={1}
            />

            <Bar
              dataKey="rentabilite"
              radius={[0, 4, 4, 0]}
              animationDuration={1500}
            >
              {displayData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.couleur} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Légende */}
      <div className="flex flex-wrap gap-4 mt-4 justify-center">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: CHART_STATUS_COLORS.excellent }} />
          <span className="text-xs text-muted-foreground">≥ 18% Excellent</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: CHART_STATUS_COLORS.attention }} />
          <span className="text-xs text-muted-foreground">10-18% Attention</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: CHART_STATUS_COLORS.deficit }} />
          <span className="text-xs text-muted-foreground">&lt; 10% Critique</span>
        </div>
      </div>

      {/* Info */}
      <div className="mt-4 flex items-start gap-2 p-3 rounded-lg bg-primary/5 border border-primary/10">
        <Target className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
        <p className="text-xs text-muted-foreground leading-relaxed">
          L'objectif de rentabilité BTP standard est de 18% minimum.
          Les chantiers sont triés du plus rentable au moins rentable.
          {data.length > 10 && ` (Affichage limité aux 10 premiers sur ${data.length})`}
        </p>
      </div>
    </div>
  );
}
